import { useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { SafeHtml } from '../../components/SafeHtml';
import { useFieldguideStore, useAuthStore } from '../../stores/root_store';
import { FieldGuideEntryEdit } from './FieldGuideEntryEdit';

const contentStyles = {
  '& img': { maxWidth: '100%', height: 'auto' },
  '& a': { color: 'primary.main' },
  '& pre': {
    bgcolor: 'action.hover',
    p: 2,
    borderRadius: 1,
    overflow: 'auto',
  },
  '& code': {
    bgcolor: 'action.hover',
    px: 0.5,
    borderRadius: 0.5,
    fontFamily: 'monospace',
  },
  '& pre code': {
    bgcolor: 'transparent',
    px: 0,
  },
  '& blockquote': {
    borderLeft: 3,
    borderColor: 'divider',
    pl: 2,
    ml: 0,
    color: 'text.secondary',
  },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
  },
  '& th, & td': {
    border: 1,
    borderColor: 'divider',
    px: 1.5,
    py: 0.75,
  },
  '& th': {
    bgcolor: 'action.hover',
    fontWeight: 'bold',
  },
};

export const FieldGuideContent = observer(() => {
  const { guideType = 'general', entrySlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fieldguideStore = useFieldguideStore();
  const authStore = useAuthStore();
  const isManager = authStore.roles.includes('manager');
  const contentRef = useRef<HTMLDivElement>(null);

  // If entrySlug is "new", render the create form
  const isCreating = entrySlug === 'new';
  const isEditing = searchParams.get('edit') === '1' && entrySlug && !isCreating;
  const sectionSlugParam = searchParams.get('section');

  // Fetch entry for editing
  useEffect(() => {
    if (isEditing && entrySlug) {
      fieldguideStore.fetchEntry(entrySlug);
    }
    return () => {
      if (isEditing) fieldguideStore.clearCurrentEntry();
    };
  }, [isEditing, entrySlug, fieldguideStore]);

  // Scroll to entry when navigating via sidebar
  useEffect(() => {
    if (entrySlug && !isCreating && !isEditing) {
      const el = document.getElementById(`entry-${entrySlug}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [entrySlug, isCreating, isEditing]);

  if (isCreating) {
    return (
      <FieldGuideEntryEdit
        sectionSlug={sectionSlugParam || undefined}
        guideType={guideType}
      />
    );
  }

  if (isEditing && entrySlug) {
    return (
      <FieldGuideEntryEdit
        entrySlug={entrySlug}
        guideType={guideType}
      />
    );
  }

  if (fieldguideStore.sectionsLoading || fieldguideStore.entriesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (fieldguideStore.sectionsError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {fieldguideStore.sectionsError}
      </Alert>
    );
  }

  if (fieldguideStore.sections.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No content yet.
        </Typography>
      </Box>
    );
  }

  // Render all sections and entries as a continuous document
  return (
    <Box ref={contentRef}>
      {fieldguideStore.sections.map((section, sectionIndex) => {
        const entries = fieldguideStore.getEntriesForSection(section.slug);

        return (
          <Box key={section.uuid} id={`section-${section.slug}`} sx={{ mb: 4 }}>
            {/* Section heading */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                pb: 1,
                borderBottom: 1,
                borderColor: 'divider',
                mb: 2,
              }}
            >
              {section.name}
            </Typography>

            {entries.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                No entries in this section yet.
                {isManager && (
                  <Button
                    size="small"
                    sx={{ ml: 1 }}
                    onClick={() =>
                      navigate(
                        `/assistant/fieldguide/${guideType}/new?section=${section.slug}`
                      )
                    }
                  >
                    Add one
                  </Button>
                )}
              </Typography>
            ) : (
              entries.map((entry, entryIndex) => {
                const isMarkdown =
                  entry.content_type === 'text/markdown' ||
                  entry.content_type === 'markdown';

                return (
                  <Box
                    key={entry.uuid}
                    id={`entry-${entry.slug}`}
                    sx={{ mb: 3, scrollMarginTop: '80px' }}
                  >
                    {/* Entry header */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 1,
                      }}
                    >
                      <Typography variant="h6">{entry.title}</Typography>
                      {isManager && (
                        <Button
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() =>
                            navigate(
                              `/assistant/fieldguide/${guideType}/${entry.slug}?edit=1`
                            )
                          }
                        >
                          Edit
                        </Button>
                      )}
                    </Box>

                    {/* Entry content */}
                    <Box sx={contentStyles}>
                      {isMarkdown ? (
                        <Markdown remarkPlugins={[remarkGfm]}>
                          {entry.content}
                        </Markdown>
                      ) : (
                        <SafeHtml content={entry.content} />
                      )}
                    </Box>

                    {/* Divider between entries (not after last) */}
                    {entryIndex < entries.length - 1 && (
                      <Divider sx={{ mt: 3 }} />
                    )}
                  </Box>
                );
              })
            )}

            {/* Divider between sections (not after last) */}
            {sectionIndex < fieldguideStore.sections.length - 1 && (
              <Divider sx={{ mt: 2 }} />
            )}
          </Box>
        );
      })}
    </Box>
  );
});
